import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function KPIResultTable({
  name,
  rows,
  companies,
  organisations,
  dates,
  selectedCompany,
  selectedDates,
  orgLength,
  activeFilter,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
}) {
  const [kpis, setKpis] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimDateKey: 'All',
    DimKeyPerformanceIndicatorKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    Target: '',
    Result: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'Target':
      case 'Result':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimKeyPerformanceIndicatorKey':
        return (a, b) => {
          const aName = getKPINameById(a[sortColumn]);
          const bName = getKPINameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    const asyncFn = () => {
      ManageReportDataApi.getKeyPerformanceIndicators()
        .then((res) => {
          if (res.status === 200) setKpis(res.data);
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    };

    asyncFn();
  }, []);

  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.sort((a, b) =>
      b.DimDateKey === a.DimDateKey
        ? a.DimKeyPerformanceIndicatorKey < b.DimKeyPerformanceIndicatorKey
          ? 1
          : -1
        : b.DimDateKey - a.DimDateKey,
    );

    setTableRows(filterRows);
  }, [rows, activeFilter, companies, selectedCompany]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const getKPINameById = (id) => {
    if (!id) return '';
    const kpi = kpis?.find(kpi => kpi[0] === id);
    return kpi ? kpi[1] + ' | ' + kpi[2] : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimDateKey',
      'DimKeyPerformanceIndicatorKey',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('DimKeyPerformanceIndicatorKey') && item.hasOwnProperty('Target') && item.hasOwnProperty('Result');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain DimKeyPerformanceIndicatorKey, Target, and Result.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (field.column.key === 'Target' || field.column.key === 'Result') {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id)) {
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      }
      return row;
    });

    const mergedRows = mergeEditedGridRows(
      tableRows,
      updatedRows,
      updated_data.rowIds,
      updated_data.updated,
    );

    if (field.column.key === 'DimDateKey') {
      mergedRows.sort((a, b) => b.DimDateKey - a.DimDateKey);
      getStateData(mergedRows);
    }

    setTableRows(mergedRows);
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };

  const kpiResultColumns = useMemo(() => {
    let kpi_result_columns = [
      SelectColumn,
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimKeyPerformanceIndicatorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>KPI</b>
            {frozenColumns.indexOf('DimKeyPerformanceIndicatorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimKeyPerformanceIndicatorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimKeyPerformanceIndicatorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimKeyPerformanceIndicatorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimKeyPerformanceIndicatorKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen:
          frozenColumns.indexOf('DimKeyPerformanceIndicatorKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        sortable: true,
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimKeyPerformanceIndicatorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimKeyPerformanceIndicatorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {kpis &&
                  kpis.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimKeyPerformanceIndicatorKey
                  ? props.row.DimKeyPerformanceIndicatorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimKeyPerformanceIndicatorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {kpis &&
                kpis.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1] + ' | ' + cur[2]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'Target',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Target</b>
            {frozenColumns.indexOf('Target') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Target')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Target')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Target')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Target')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Target}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Target: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Target') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Target}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Target: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'Result',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Result</b>
            {frozenColumns.indexOf('Result') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Result')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Result')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Result')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Result')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Result}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Result: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Result') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.Result}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                Result: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
    ];

    if (orgLength.length > 1) {
      kpi_result_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      kpi_result_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = kpi_result_columns.filter((col) => col.frozen === true);
    let unfrozenCols = kpi_result_columns.filter((col) => col.frozen === false);
    kpi_result_columns = [...frozenCols, ...unfrozenCols];

    return kpi_result_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    kpis,
    dates,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimKeyPerformanceIndicatorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimKeyPerformanceIndicatorKey ==
            filters.DimKeyPerformanceIndicatorKey
          : true) &&
        (filters.Result
          ? r.Result.toString().includes(filters.Result.toString())
          : true) &&
        (filters.Target
          ? r.Target.toString().includes(filters.Target.toString())
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportData = useMemo(() => {
    let export_data = [];

    if (exportAllData) {
      export_data = [...tableRows];
    } else {
      if (startDate && endDate) {
        let start_date =
          new Date(startDate).getFullYear() * 10000 +
          (new Date(startDate).getMonth() + 1) * 100 +
          1;
        let end_date =
          new Date(endDate).getFullYear() * 10000 +
          (new Date(endDate).getMonth() + 1) * 100 +
          1;
        export_data = [...tableRows];
        export_data = export_data.filter(
          (data) =>
            data.DimDateKey >= start_date && data.DimDateKey <= end_date,
        );
      }
    }

    return export_data;
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    let export_value = {
      KPI: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
      KeyPerformanceIndicator: {
        columns: [],
        data: [],
      },
    };

    export_value.KPI.columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'DimDateKey', width: { wpx: 80 } },
      { title: 'DimKeyPerformanceIndicatorKey', width: { wpx: 80 } },
      { title: 'Target', width: { wpx: 80 } },
      { title: 'Result', width: { wpx: 80 } },
    ];
    exportData.forEach((data) => {
      export_value.KPI.data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimKeyPerformanceIndicatorKey
            ? Number(data.DimKeyPerformanceIndicatorKey)
            : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.Target ? Number(data.Target) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.Result ? Number(data.Result) : 0,
          style: { numFmt: '0.00' },
        },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    export_value.KeyPerformanceIndicator.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    kpis &&
      kpis.forEach((cur) => {
        export_value.KeyPerformanceIndicator.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] + ' | ' + cur[2] },
        ]);
      });

    return export_value;
  }, [exportData, companies, organisations, dates, kpis]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <Button
          onClick={() => onClickedAdd(name, 'auto')}
          variant="outlined"
          color="success"
        >
          Auto Add
        </Button>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={kpiResultColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel filename="KPI" value={exportValue} />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

KPIResultTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default KPIResultTable;
